import type { MutableRefObject } from "react";
import { useEffect, useRef, useState } from "react";

export interface ITableMeasurement {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface ITableMeasurements {
  table: ITableMeasurement | null;
  cols: ITableMeasurement[];
  rows: ITableMeasurement[];
}

const EMPTY: ITableMeasurements = {
  table: null,
  cols: [],
  rows: [],
};

const measure = (
  wrapper: HTMLElement,
  tableCols: number,
  tableRows: number,
): ITableMeasurements => {
  const table = wrapper.querySelector("table");
  if (!table) return EMPTY;

  const wrapperRect = wrapper.getBoundingClientRect();
  const tableRect = table.getBoundingClientRect();

  const relTable: ITableMeasurement = {
    left: tableRect.left - wrapperRect.left,
    top: tableRect.top - wrapperRect.top,
    width: tableRect.width,
    height: tableRect.height,
  };

  const cols: ITableMeasurement[] = Array.from({ length: tableCols });
  const rows: ITableMeasurement[] = Array.from({ length: tableRows });

  const cellEls = Array.from(
    table.querySelectorAll<HTMLTableCellElement>(
      "[data-cell-col][data-cell-row]",
    ),
  );
  for (const cellEl of cellEls) {
    const col = Number(cellEl.getAttribute("data-cell-col"));
    const row = Number(cellEl.getAttribute("data-cell-row"));
    const colSpan = cellEl.colSpan || 1;
    const rowSpan = cellEl.rowSpan || 1;
    const rect = cellEl.getBoundingClientRect();

    if (colSpan === 1 && cols[col] == null) {
      cols[col] = {
        left: rect.left - tableRect.left,
        top: 0,
        width: rect.width,
        height: 0,
      };
    }
    if (rowSpan === 1 && rows[row] == null) {
      rows[row] = {
        left: 0,
        top: rect.top - tableRect.top,
        width: 0,
        height: rect.height,
      };
    }
  }

  for (let c = 0; c < tableCols; c++) {
    if (cols[c] == null) {
      const prev = c > 0 ? cols[c - 1] : null;
      const fallbackLeft = prev ? prev.left + prev.width : 0;
      const fallbackWidth = tableRect.width / tableCols;
      cols[c] = {
        left: fallbackLeft,
        top: 0,
        width: fallbackWidth,
        height: 0,
      };
    }
  }
  for (let r = 0; r < tableRows; r++) {
    if (rows[r] == null) {
      const prev = r > 0 ? rows[r - 1] : null;
      const fallbackTop = prev ? prev.top + prev.height : 0;
      const fallbackHeight = tableRect.height / tableRows;
      rows[r] = {
        left: 0,
        top: fallbackTop,
        width: 0,
        height: fallbackHeight,
      };
    }
  }

  return { table: relTable, cols, rows };
};

const sameRect = (a: ITableMeasurement | null, b: ITableMeasurement | null) => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.left === b.left &&
    a.top === b.top &&
    a.width === b.width &&
    a.height === b.height
  );
};

const sameList = (a: ITableMeasurement[], b: ITableMeasurement[]) => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (!sameRect(a[i], b[i])) return false;
  }
  return true;
};

const sameMeasurements = (a: ITableMeasurements, b: ITableMeasurements) => {
  return (
    sameRect(a.table, b.table) &&
    sameList(a.cols, b.cols) &&
    sameList(a.rows, b.rows)
  );
};

export const useTableMeasurements = (
  wrapperRef: MutableRefObject<HTMLElement | null>,
  tableCols: number,
  tableRows: number,
  enabled: boolean,
  // Row reorder doesn't change tableRows / tableCols counts, and ResizeObserver
  // only fires on size changes — not position changes. So when two rows of
  // different heights swap places, divider lines stay anchored at the old
  // `top` values until something else triggers a remeasure. Including the
  // row-heights / col-widths array references here forces a fresh measure
  // whenever they're reassigned by reorder/resize/layout actions.
  tableRowHeights?: readonly number[],
  tableColWidths?: readonly number[],
): ITableMeasurements => {
  const [measurements, setMeasurements] = useState<ITableMeasurements>(EMPTY);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      setMeasurements(EMPTY);
      return;
    }
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const run = () => {
      rafRef.current = null;
      const next = measure(wrapper, tableCols, tableRows);
      setMeasurements((prev) => (sameMeasurements(prev, next) ? prev : next));
    };
    const schedule = () => {
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(run);
    };

    schedule();

    // The table's total width stays constant when a column is resized
    // (cols are %-based and redistribute), so observing the <table>
    // alone doesn't detect interior column changes. Add one entry per
    // first-row cell so col-width changes fire the observer. First-col
    // cells are NOT observed — total table height DOES change when row
    // heights change (rows grow the table), so the <table> observation
    // covers those.
    const table = wrapper.querySelector("table");
    const ro = new ResizeObserver(schedule);
    if (table) {
      ro.observe(table);
      const firstRowCells = table.querySelectorAll<HTMLElement>(
        '[data-cell-row="0"]',
      );
      for (let i = 0; i < firstRowCells.length; i++) {
        ro.observe(firstRowCells[i]);
      }
    }
    ro.observe(wrapper);

    window.addEventListener("resize", schedule);

    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      ro.disconnect();
      window.removeEventListener("resize", schedule);
    };
  }, [wrapperRef, tableCols, tableRows, enabled, tableRowHeights, tableColWidths]);

  return measurements;
};
